/**
 * Cleo ERP SaaS — Liste des tenants admin (cadrage §3.5)
 *
 * Tableau avec colonnes : Slug, Entreprise, Pays, Statut, Inscription, Expiration, Actions.
 * Filtres : par statut, par pays, recherche par nom/email.
 * Lien stats en haut.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Tag, Input, Select, Card, Typography, Space,
  Spin, Alert, Row, Col, Statistic,
} from 'antd';
import {
  SearchOutlined, TeamOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text } = Typography;

// Couleurs badges statut (cadrage §3.6)
const STATUS_COLORS = {
  EN_ATTENTE: '#F59E0B',
  ACTIF: '#10B981',
  'EXPIRÉ': '#EF4444',
  SUSPENDU: '#64748B',
  A_SUPPRIMER: '#991B1B',
  'SUPPRIMÉ': '#CBD5E1',
};

const STATUS_LABELS = {
  EN_ATTENTE: 'En attente',
  ACTIF: 'Actif',
  'EXPIRÉ': 'Expiré',
  SUSPENDU: 'Suspendu',
  A_SUPPRIMER: 'À supprimer',
  'SUPPRIMÉ': 'Supprimé',
};

const COUNTRY_LABELS = {
  MA: 'Maroc', CI: "Côte d'Ivoire", SN: 'Sénégal', ML: 'Mali',
  BF: 'Burkina Faso', TG: 'Togo', BJ: 'Bénin', NE: 'Niger',
  GN: 'Guinée', FR: 'France',
};

function TenantListPage() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', country: '', search: '' });
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.country) params.country = filters.country;
      if (filters.search) params.search = filters.search;

      const [tenantsRes, statsRes] = await Promise.all([
        api.get('/api/admin/tenants/', { params }),
        api.get('/api/admin/stats/'),
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError('Impossible de charger les données.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const columns = [
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug, record) => (
        <a onClick={() => navigate(`/admin/tenants/${record.id}`)}
           style={{ fontWeight: 600, color: '#0F172A' }}>
          {slug}
        </a>
      ),
    },
    {
      title: 'Entreprise',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: 'Pays',
      dataIndex: 'country_code',
      key: 'country_code',
      render: (code) => COUNTRY_LABELS[code] || code,
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={STATUS_COLORS[status] || '#CBD5E1'}
          style={{ borderRadius: 6, fontSize: 12 }}
        >
          {STATUS_LABELS[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Port',
      dataIndex: 'internal_port',
      key: 'internal_port',
      render: (port) => <Text style={{ color: '#64748B', fontSize: 13 }}>{port}</Text>,
    },
    {
      title: 'Inscription',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Expiration',
      dataIndex: 'trial_end_date',
      key: 'trial_end_date',
      render: (date, record) => {
        if (!date) return <Text style={{ color: '#94A3B8' }}>—</Text>;
        const isExpired = record.days_remaining === 0;
        return (
          <Text style={{ color: isExpired ? '#EF4444' : '#0F172A' }}>
            {new Date(date).toLocaleDateString('fr-FR')}
            {record.days_remaining > 0 && (
              <Text style={{ color: '#64748B', fontSize: 12, marginLeft: 4 }}>
                (J-{record.days_remaining})
              </Text>
            )}
          </Text>
        );
      },
    },
  ];

  if (error) {
    return <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />;
  }

  return (
    <div>
      <Title level={4} style={{ color: '#0F172A', marginBottom: 16 }}>
        Tenants
      </Title>

      {/* Stats rapides */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
              <Statistic
                title={<Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase' }}>Total</Text>}
                value={stats.total_tenants}
                prefix={<TeamOutlined style={{ color: '#0F172A' }} />}
                valueStyle={{ color: '#0F172A', fontSize: 22, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
              <Statistic
                title={<Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase' }}>Actifs</Text>}
                value={stats.actifs}
                prefix={<CheckCircleOutlined style={{ color: '#10B981' }} />}
                valueStyle={{ color: '#10B981', fontSize: 22, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
              <Statistic
                title={<Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase' }}>En attente</Text>}
                value={stats.en_attente}
                prefix={<ClockCircleOutlined style={{ color: '#F59E0B' }} />}
                valueStyle={{ color: '#F59E0B', fontSize: 22, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
              <Statistic
                title={<Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase' }}>Expirés</Text>}
                value={stats.expires}
                prefix={<ExclamationCircleOutlined style={{ color: '#EF4444' }} />}
                valueStyle={{ color: '#EF4444', fontSize: 22, fontWeight: 700 }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtres */}
      <Card size="small" style={{
        borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 16,
      }}>
        <Space wrap size={12}>
          <Input
            placeholder="Rechercher slug, nom, email..."
            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
            style={{ width: 260 }}
            allowClear
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
          <Select
            placeholder="Statut"
            style={{ width: 150 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, status: val || '' }))}
            options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v }))}
          />
          <Select
            placeholder="Pays"
            style={{ width: 160 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, country: val || '' }))}
            options={Object.entries(COUNTRY_LABELS).map(([k, v]) => ({ value: k, label: v }))}
          />
        </Space>
      </Card>

      {/* Tableau */}
      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <Table
          columns={columns}
          dataSource={tenants}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="middle"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/tenants/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
}

export default TenantListPage;
